import { ApplicationConfig, importProvidersFrom, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { registerLocaleData } from '@angular/common';
import localeEsCL from '@angular/common/locales/es-CL';

import { routes } from './app.routes';
import { ToastrModule } from 'ngx-toastr';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';

import { ConfigService } from './services/config.service';
import { BusinessContextService } from './services/business-context.service';
import { KeycloakAngularModule, KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular';
import { AuthErrorInterceptor } from './auth/auth-error.interceptor';
import { BusinessHttpInterceptor } from './auth/business-http.interceptor';
import { ServerErrorInterceptor } from './auth/server-error.interceptor';


// Register Spanish locale data
registerLocaleData(localeEsCL);

// Single initializer: load config first, then init Keycloak using loaded values
export function initializeApp(keycloak: KeycloakService, config: ConfigService) {
  return async () => {
    // 1) Load configuration
    await config.loadConfig();

    // 2) Initialize Keycloak only if config is present
    const url = (config as any).keycloakUrl;
    const realm = (config as any).keycloakRealm;
    const clientId = (config as any).keycloakClientId;
    console.log('[Keycloak] Loaded config:', { url, realm, clientId });

    if (!url || !realm || !clientId) {
      console.warn('Keycloak configuration is missing; skipping init');
      return;
    }

    const isDev = (config as any).isDevelopment;
    console.log('[Keycloak] isDevelopment =', isDev);

    // Detectar si hay tokens guardados (login propio)
    const hasStoredTokens =
      !!localStorage.getItem('KC_ACCESS_TOKEN') &&
      !!localStorage.getItem('KC_REFRESH_TOKEN');

    // No usar onLoad=check-sso ni login-required para evitar que Keycloak
    // intente un browser login (standard flow) cuando el cliente lo tiene
    // deshabilitado. Solo inicializamos con los tokens si existen.
    const initOptionsAny: any = {
      checkLoginIframe: false,
      silentCheckSsoFallback: false,
      checkThirdPartyCookies: false,
    };
    if (!hasStoredTokens) {
      console.warn('[Keycloak] No stored tokens; skipping browser login/SSO check on init');
    }

    // Inyectar tokens de localStorage si están presentes
    try {
      const sanitize = (v: string | null) =>
        (v || '').toString().trim().replace(/^\s*["']+|["']+\s*$/g, '');
      const access = sanitize(localStorage.getItem('KC_ACCESS_TOKEN'));
      const refresh = sanitize(localStorage.getItem('KC_REFRESH_TOKEN'));
      const id = sanitize(localStorage.getItem('KC_ID_TOKEN'));
      if (access && refresh) {
        (initOptionsAny as any).token = access;
        (initOptionsAny as any).refreshToken = refresh;
        if (id) (initOptionsAny as any).idToken = id;
        console.log('[Keycloak] Init con tokens de localStorage');
      }
    } catch (e) {
      console.warn('[Keycloak] No se pudo leer tokens del localStorage:', e);
    }

    try {
      await keycloak.init({
        config: { url, realm, clientId },
        initOptions: initOptionsAny,
        enableBearerInterceptor: true,
        bearerPrefix: 'Bearer',
        loadUserProfileAtStartUp: false,
      });
    } catch (e) {
      console.warn('[Keycloak] Init failed, clearing stored KC_* tokens:', e);
      try {
        localStorage.removeItem('KC_ACCESS_TOKEN');
        localStorage.removeItem('KC_REFRESH_TOKEN');
        localStorage.removeItem('KC_ID_TOKEN');
        localStorage.removeItem('KC_TOKEN_ISSUED_AT');
      } catch {}
      // Continuar sin sesión inicial; el login por formulario seguirá disponible
    }

    // No forzar login aquí: la vista pública y los guards manejarán el acceso
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideRouter(routes),
    provideAnimations(),
    importProvidersFrom([
      KeycloakAngularModule,
      ToastrModule.forRoot({
        positionClass: 'toast-top-right',
        preventDuplicates: true,
      }),
      ReactiveFormsModule,
      // Material Modules
      MatButtonModule,
      MatIconModule,
      MatTooltipModule,
      MatInputModule,
      MatFormFieldModule,
      MatTableModule,
      MatSortModule,
      MatPaginatorModule
    ]),
    { provide: LOCALE_ID, useValue: 'es-CL' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [KeycloakService, ConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BusinessHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ServerErrorInterceptor,
      multi: true,
    },
  ]
};
